// Sentry MUST be the first import so errors during module evaluation of
// the rest of the app are captured. See `./sentry.ts` for init details.
import { Sentry } from './sentry';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './index.css';
import { App } from './App';
import { AuthSessionProvider } from './features/auth/hooks/useAuthSession';
import { Toaster } from './components/ui/sonner';

/**
 * F-007 Phase 4 (IMPL-P4-001) — bootstrap globale di TanStack Query + sonner.
 *
 * - QueryClient con default conservativi (staleTime 1 min, retry 1) condivisi
 *   da tutta l'app: prima feature consumatrice è `features/operatore`.
 * - <Toaster> di sonner mountato dentro al QueryClientProvider per consentire
 *   ai mutation `onError`/`onSuccess` di chiamare `toast.error(...)` (es. il
 *   conflitto 409 su take-in-carico).
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1_000 * 60,
      retry: 1,
    },
  },
});

function ErrorFallback(): JSX.Element {
  return (
    <main className="min-h-screen bg-background text-foreground flex items-center justify-center p-6">
      <div className="max-w-md text-center space-y-4">
        <h1 className="text-2xl font-semibold">Qualcosa è andato storto</h1>
        <p className="text-sm text-muted-foreground">
          Abbiamo registrato l'errore. Ricarica la pagina per riprovare.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center rounded-md border bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Ricarica
        </button>
      </div>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthSessionProvider>
            <App />
          </AuthSessionProvider>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
